import type { ColumnDef } from "@coval/ui"

import {
  isCollectionFilterActive,
  type CollectionFilterControls,
} from "@/hooks/useCollectionFilters"

import { convertTableDataToCSV } from "./csv-export"

export type CsvColumnDef<T> = ColumnDef<T> & {
  csvValue: (row: T) => unknown
}

export interface TableExportScope {
  search?: string
  filterControls?: CollectionFilterControls
  filters?: Record<string, unknown>
}

function describeValue(value: unknown): string {
  if (value == null || value === "") {
    return ""
  }
  if (Array.isArray(value)) {
    return value.map(describeValue).filter(Boolean).join(", ")
  }
  if (value instanceof Date) {
    return value.toLocaleString()
  }
  if (typeof value === "object") {
    if ("operator" in value && ("metricId" in value || "metricName" in value)) {
      const condition = value as Record<string, unknown>
      const name =
        condition.metricDisplayName ??
        condition.metricName ??
        condition.metricId
      const operator =
        condition.operator === "exists" ? "has any value" : condition.operator
      const target =
        condition.operator === "exists" ? "" : describeValue(condition.value)
      return `${describeValue(name)} ${describeValue(operator)} ${target}${condition.rangeMax != null ? ` – ${describeValue(condition.rangeMax)}` : ""}`.trim()
    }
    return Object.entries(value)
      .map(([key, item]) => {
        const text = describeValue(item)
        return text ? `${key}: ${text}` : ""
      })
      .filter(Boolean)
      .join(" · ")
  }
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
    ? String(value)
    : ""
}

export function describeTableFilters({
  search,
  filterControls,
  filters,
}: TableExportScope): string[] {
  const summaries = search?.trim() ? [`Search: ${search.trim()}`] : []
  filterControls?.properties.forEach((property) => {
    const value = filterControls.values[property.id] ?? {}
    if (!isCollectionFilterActive(value)) {
      return
    }
    const summary =
      value.selected
        ?.map(
          (id) =>
            property.options.find((option) => option.id === id)?.label ?? id,
        )
        .join(", ") ||
      value.text ||
      `${value.min || "Any"} – ${value.max || "Any"}`
    summaries.push(`${property.label}: ${summary}`)
  })
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    const summary = describeValue(value)
    const label = key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replaceAll("_", " ")
      .replace(/Filters?$/i, "")
      .trim()
    if (summary) {
      summaries.push(
        `${label.charAt(0).toUpperCase()}${label.slice(1)}: ${summary}`,
      )
    }
  })
  return summaries
}

export function buildBrowseTableCsv<T extends object>({
  rows,
  columns,
  visibility,
  columnOrder,
  getSubRows,
  isExportRow,
  getRowId,
}: {
  rows: T[]
  columns: ColumnDef<T>[]
  visibility: Record<string, boolean>
  columnOrder?: string[]
  getSubRows?: (row: T) => T[] | undefined
  isExportRow?: (row: T) => boolean
  getRowId: (row: T) => string
}) {
  const exportColumns = columns.filter(
    (column): column is CsvColumnDef<T> =>
      (visibility[column.id] ?? true) &&
      "csvValue" in column &&
      typeof column.csvValue === "function",
  )
  const order =
    columnOrder ??
    [
      ...columns.filter((column) => column.variant !== "id"),
      ...columns.filter((column) => column.variant === "id"),
    ].map((column) => column.id)
  const rank = new Map(order.map((id, index) => [id, index]))
  exportColumns.sort(
    (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity),
  )
  const flatRows: T[] = []
  const seen = new Set<string>()
  const visit = (items: T[]) => {
    items.forEach((row) => {
      const children = getSubRows?.(row)
      if (children?.length) {
        visit(children)
      } else if (
        (!isExportRow || isExportRow(row)) &&
        !seen.has(getRowId(row))
      ) {
        seen.add(getRowId(row))
        flatRows.push(row)
      }
    })
  }
  visit(rows)
  const labels = exportColumns.map(
    (column) =>
      column.headerLabel ??
      (typeof column.header === "string" ? column.header : column.id),
  )
  return {
    rowCount: flatRows.length,
    columns: labels,
    createCsv: () =>
      convertTableDataToCSV(
        flatRows,
        exportColumns.map((column, index) => ({
          header: labels[index],
          value: column.csvValue,
        })),
      ),
  }
}
